import React, { useCallback, useEffect, useState } from 'react'
import {
  joinCHeckByPcodeQueryOption,
  termsListQueryOption,
  uiInfoQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { useParamsList } from 'src/hooks/useParamsList.hook'
import {
  CertNumberRequestSpace,
  CertNumberVerifySpace,
  JoinPossibleCheckByPcode,
  KTMVNOVerifySpace,
  VulnerableVerifySpace,
  TelecomNumberType,
  TermsListSpace,
  termsValueNumberType,
  UIInfoSpace,
} from 'src/types/api'
import { FormProvider, SubmitErrorHandler, SubmitHandler } from 'react-hook-form'
import useDefaultJoinForm from 'src/hooks/react-hook-form/useDefaultJoinForm'
import { getTermsErrorMessage } from 'src/hooks/react-hook-form/shared/getTermsErrorMessage'
import DefaultJoinFormPageView from 'src/pages/DefaultJoinFormPage/view'
import { FormObjectType, JoinFormType } from 'src/types/react-hook-form'
import {
  certRequestQuery,
  certVerifyQuery,
  KTMVNOVerifyQuery,
  VulnerableVerifyQuery,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import Loading from 'src/components/Loading'
import {
  ERRCODE_SUCCESS,
  ERRCODES_OTPCERTREQUEST,
  ERRCODES_OTPCERTSUBMIT,
} from 'src/types/errcodes'
import { useCountdown } from 'src/hooks/useCountDown.hook'
import {
  already_signup_dataLayer,
  d30_resignup_dataLayer,
  join_complete_dataLayer,
  otp_request_dataLayer,
} from 'src/service/utils/gtmDatalayer'
import { deviceDetection, handleMoveToAppStore, handleMoveToSvcLoginPage } from 'src/service/utils'
import { parseUiData } from 'src/service/utils/decode'

import RenderIf from 'src/components/RenderIf'
import useSetTimeout from 'src/hooks/useSetTimeout.hook'
import KTMVNOModalChildren from 'src/components/Modal/_component/KtMvnoModalChildren'
import { useModal } from 'src/components/Modal/provider'
import { dynamicUiDefaultData } from 'src/service/constants'
import usePcodeMatch from 'src/hooks/usePcodeMatch.hook'
import NmpJoinFormPageView from 'src/pages/NmpJoinFormPage/view'
import { useParams } from 'react-router-dom'
import { join_complete_greenp, landing_greenp } from 'src/service/utils/greenp'
import { join_complete_tnk } from 'src/service/utils/tnk'
import VulnerableCheckModal from 'src/components/Modal/_component/VulnerableCheckModal'
import TermsModal from 'src/components/Modal/_component/TermsModal'
import { captureMvnoTermsError } from 'src/service/utils/sentry/validation'

export interface DefaultJoinFormPageProps {
  dynamicUiData: Omit<UIInfoSpace.Response, 'UIData'> & { UIData: UIInfoSpace.UIDataType }
  registerAttribute: Record<keyof JoinFormType, Partial<FormObjectType>>
  otpCertRequest: {
    submit: SubmitHandler<Pick<JoinFormType, 'PNumber'>>
    error: SubmitErrorHandler<JoinFormType>
  }
  otpCertVerify: {
    submit: SubmitHandler<JoinFormType>
    error: SubmitErrorHandler<JoinFormType>
  }
  resetFormItems: () => void
  registerAttributeExcludeAllTerms: Partial<FormObjectType>[]
  count: string
  blockTelList: string[] | null
}
const GREENP_TRACKING_FORMS = ['rop01', 'mkt01'] //greenp 연동 페이지
const KT_PASS_FORMS = ['rop01', 'mkt01']
const TNK_TRACKING_PCODES = ['tnk_01']

const DefaultJoinFormPage = () => {
  const [blockTelList, setBlockTelList] = useState<string[] | null>(null)
  const { pCode, fixedTelecom, telecom, requestId, slotNo } = useParamsList()
  const { form } = useParams()
  const { setCustomTimeout } = useSetTimeout()
  const { openModal, closeModal } = useModal()
  const { nmp_e01 } = usePcodeMatch()

  const isGreenpTracking = GREENP_TRACKING_FORMS.includes(form as string)
  const isTnkTracking = TNK_TRACKING_PCODES.includes(pCode as string)
  const isAosKtPass =
    KT_PASS_FORMS.includes(form as string) &&
    deviceDetection() === 'AOS' &&
    pCode.split('_')[0] === 'ktpass'
  const isRedirectAosAppStore = isAosKtPass

  const {
    data: { Body: { UIData, ...dynamicUi } = dynamicUiDefaultData } = {},
    isPending: isDynamicUiPending,
  } = useCustomQuery<UIInfoSpace.Response>(uiInfoQueryOption(pCode as string, slotNo))

  const { data: { Body: termsContents = undefined } = {} } =
    useCustomQuery<TermsListSpace.Response>(termsListQueryOption())

  const { data: { Body: joinPossibleData = undefined } = {}, isSuccess: isJoinPossibleSuccess } =
    useCustomQuery<JoinPossibleCheckByPcode.Response>(joinCHeckByPcodeQueryOption(pCode))

  const { isPending: isOtpRequestPending, mutateAsync: otpRequestMutate } = useCustomMutation<
    CertNumberRequestSpace.Request,
    CertNumberRequestSpace.Response
  >(certRequestQuery())

  const { isPending: isOtpVerifyPending, mutateAsync: otpVerifyMutate } = useCustomMutation<
    CertNumberVerifySpace.Request,
    CertNumberVerifySpace.Response
  >(certVerifyQuery())
  const { isPending: isMvnoVerifyPending, mutateAsync: ktMvnoVerifyMutate } = useCustomMutation<
    KTMVNOVerifySpace.Request,
    KTMVNOVerifySpace.Response
  >(KTMVNOVerifyQuery())

  const { isPending: isVulnerableVerifyPending, mutateAsync: vulnerableVerifyMutate } =
    useCustomMutation<VulnerableVerifySpace.Request, VulnerableVerifySpace.Response>(
      VulnerableVerifyQuery(),
    )

  const {
    methods,
    registerAttribute,
    allTermsAgreeHandler,
    termsData,
    isTermsModalOpen,
    setIsTermsModalOpen,
    termsUrl,
  } = useDefaultJoinForm(termsContents)
  const { setFocus, setValue, reset, getValues, resetField } = methods

  const ktMVNOAttribute = registerAttribute.KTMVNO

  const handleFocusApprovalNumber = () => {
    setTimeout(() => {
      setFocus('ApprovalNumber')
      window.onfocus = () => setFocus('ApprovalNumber')
    }, 200)
  }

  //인증번호 유효시간 초과
  const { countStart, count, countStop } = useCountdown({
    countExpiredCallbackFn: () => {
      resetField('ApprovalNumber')
      alert('유효시간이 초과되었습니다. 인증번호를 다시 받으시기 바랍니다.')
      closeModal()
    },
  })

  const handleCheckKtMvnoUser = async () => {
    const Telecom = Number(fixedTelecom || telecom) as TelecomNumberType
    const ktAgency = Telecom === 1
    if (!ktAgency || getValues('KTMVNO')) return true

    return await ktMvnoVerifyMutate({ PNumber: getValues('PNumber'), Telecom }).then((res) => {
      captureMvnoTermsError(res.Body)

      const isMVNO = res.Body.Type === 1
      if (!isMVNO) return true
      else {
        openModal({
          contents: {
            children: (
              <FormProvider {...methods}>
                <KTMVNOModalChildren
                  {...res.Body}
                  ktMVNOAttribute={ktMVNOAttribute}
                  otpCertRequest={() => otpCertRequest.submit({ PNumber: getValues('PNumber') })}
                />
              </FormProvider>
            ),
          },
        })
        return false
      }
    })
  }

  const handleCheckVulnerableUser = async () => {
    const Telecom = Number(fixedTelecom || telecom) as TelecomNumberType

    return await vulnerableVerifyMutate({ PNumber: getValues('PNumber'), Telecom }).then((res) => {
      const { Vulnerable, AgePopUpYN } = res.Body
      const isVulnerable = Vulnerable === 'Y' || AgePopUpYN === 'Y'
      if (!isVulnerable) return true

      return new Promise<boolean>((resolve) => {
        openModal({
          contents: {
            children: (
              <VulnerableCheckModal
                onConfirm={() => {
                  resolve(true)
                }}
                onCancel={() => {
                  resolve(false)
                }}
                telecom={Telecom}
              />
            ),
          },
        })
      })
    })
  }
  const otpCertRequest: {
    submit: SubmitHandler<Pick<JoinFormType, 'PNumber'>>
    error: SubmitErrorHandler<JoinFormType>
  } = {
    submit: (data) => {
      const Telecom = Number(fixedTelecom || telecom) as TelecomNumberType
      const { PNumber } = data

      handleCheckKtMvnoUser().then((isNotKtMvno) => {
        if (isNotKtMvno) {
          handleOtpRequest()
        }
      })

      const handleOtpRequest = () => {
        otpRequestMutate({ PNumber, Telecom, PCode: pCode, TimeOut: 180 })
          .then((res) => {
            const { Body, Header } = res
            switch (Header.ErrCode) {
              case ERRCODE_SUCCESS:
                const { TranID, TimeOut } = Body
                setValue('TranID', TranID)
                countStart(TimeOut)
                otp_request_dataLayer()
                alert(
                  '입력하신 번호로 인증번호가 전송되었습니다. 전송된 인증번호를 입력하여 주세요.',
                )

                document
                  .getElementById('middle_Image')
                  ?.scrollIntoView({ inline: 'start', block: 'start', behavior: 'auto' })

                handleFocusApprovalNumber()
                break
              case ERRCODES_OTPCERTREQUEST.Error5timesLimit:
              default:
                alert(Header.ErrMsg)
                countStop()
                break
              case ERRCODES_OTPCERTREQUEST.ErrorAlreadyMember:
                alert(Header.ErrMsg)
                handleMoveToAppStore()
                already_signup_dataLayer()
                resetFormItems()
                break
              case ERRCODES_OTPCERTREQUEST.Error30DaysLimit:
                d30_resignup_dataLayer()
                alert(Header.ErrMsg)
                break
              case ERRCODES_OTPCERTREQUEST.ErrorSpMember:
                alert(Header.ErrMsg)
                resetFormItems()
                break
              case ERRCODES_OTPCERTREQUEST.ErrorPNumber:
              case ERRCODES_OTPCERTREQUEST.ErrorNoTelecom:
              case ERRCODES_OTPCERTREQUEST.ErrorAuthMsgTime:
                alert(Header.ErrMsg)
                break
            }
          })
          .catch((err: Error) => {
            console.error(err)
          })
      }
    },
    error: (errors, event) => {
      const { PNumber } = errors
      const errorMsg = PNumber?.message
      const termsErrorMsg = getTermsErrorMessage(errors)

      if (!!errorMsg) {
        alert(errorMsg)
        window.focus()
        window.onfocus = () => {
          setCustomTimeout(() => setFocus('PNumber', { shouldSelect: true }), 500)
        }
      } else if (!!termsErrorMsg) {
        const handleConfirm = window.confirm(termsErrorMsg)
        if (handleConfirm) {
          allTermsAgreeHandler()
          otpCertRequest.submit({ PNumber: getValues('PNumber') })
        } else return
      }
      console.warn(errors)
    },
  }

  const otpCertVerify: {
    submit: SubmitHandler<JoinFormType>
    error: SubmitErrorHandler<JoinFormType>
  } = {
    submit: (data) => {
      const {
        PNumber,
        TranID,
        ApprovalNumber,
        Service,
        SKTPrivacy,
        KTPrivacy,
        LGUPPrivacy,
        Insurance,
        PrivacyConsent,
        PrivacyThirdParty,
        Marketing,
        KTMVNO,
      } = data

      const otpVerifySubmitData: CertNumberVerifySpace.Request = {
        Telecom: Number(telecom) as TelecomNumberType,
        PNumber,
        TranID,
        ApprovalNumber,
        TermsIdx: termsData?.Idx,
        AgreeDetail: {
          ServiceAgree: Number(Service) as termsValueNumberType,
          PrivacyConsentAgree: Number(PrivacyConsent) as termsValueNumberType,
          PrivacyThirdPartyAgree: Number(PrivacyThirdParty) as termsValueNumberType,
          SKTPrivacyAgree: Number(SKTPrivacy) as termsValueNumberType,
          KTPrivacyAgree: Number(KTPrivacy) as termsValueNumberType,
          LGUPPrivacyAgree: Number(LGUPPrivacy) as termsValueNumberType,
          InsuranceAgree: Number(Insurance) as termsValueNumberType,
          MarketingAgree: Number(Marketing) as termsValueNumberType,
          KTMVNOAgree: Number(KTMVNO) as termsValueNumberType,
        },
        PCode: pCode,
        //reward api authkey (reward 존재 시)
        AuthKey: '',
      }
      if (getValues('ApprovalNumber')?.length === 0) {
        alert('인증번호를 입력해주세요.')
        return
      }
      handleCheckVulnerableUser().then((isVulnerable) => {
        if (isVulnerable) {
          handleOtpVerify()
        }
      })

      const handleOtpVerify = () => {
        otpVerifyMutate(otpVerifySubmitData)
          .then((res) => {
            const { Header } = res
            switch (Header.ErrCode) {
              case ERRCODE_SUCCESS:
                if (isGreenpTracking) join_complete_greenp(requestId)
                if (isTnkTracking) join_complete_tnk()
                join_complete_dataLayer(PNumber)

                if (isRedirectAosAppStore) {
                  alert(
                    '휴대폰쿠폰지갑 안내\n휴대폰에서 모바일쿠폰을 상시 할인 및 특가에 구매할 수 있는 유료부가서비스에 가입되셨습니다. 원활한 이용을 위해 [확인] 버튼을 눌러 앱스토어로 이동하세요.',
                  )
                  handleMoveToAppStore({ skipCloseFallback: true })
                } else {
                  alert(
                    '휴대폰쿠폰지갑 안내\n휴대폰에서 모바일쿠폰을 상시 할인 및 특가에 구매할 수 있는 유료부가서비스에 가입되셨습니다.\n원활한 이용을 위해 [확인] 버튼을 눌러 서비스페이지로 이동하세요.',
                  )
                  handleMoveToSvcLoginPage(pCode, Number(telecom) as TelecomNumberType, PNumber)
                }
                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNoRequest:
                alert(Header.ErrMsg)
                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNumberWrong:
                alert(Header.ErrMsg)
                resetField('ApprovalNumber')
                handleFocusApprovalNumber()
                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNumber3Wrong:
                alert(Header.ErrMsg)
                window.location.reload()
                break
            }
          })
          .catch((err: Error) => {
            console.error(err)
          })
      }
    },
    error: (errors, event) => {
      const { PNumber, TranID } = errors
      const errorMsg = TranID?.message || PNumber?.message
      const termsErrorMsg = getTermsErrorMessage(errors)

      switch (true) {
        case getValues('TranID') === '':
          alert('인증번호 받기를 먼저 시도하여 주세요.')
          break
        case !!errorMsg:
          alert(errorMsg)
          break
        case !!termsErrorMsg:
          const handleConfirm = window.confirm(termsErrorMsg)
          if (handleConfirm) {
            allTermsAgreeHandler()
            setCustomTimeout(() => otpCertVerify.submit({ ...getValues() }), 500)
          } else return
          break
        default:
          return
      }
      console.warn(errors)
    },
  }

  const registerAttributeExcludeAllTerms: () => Partial<FormObjectType>[] = useCallback(() => {
    const objectEntriesAttribute = Object.entries(registerAttribute)

    return objectEntriesAttribute
      .filter((attributeArray) => attributeArray[0] !== 'allTerms')
      .map((attribute) => attribute[1])
      .filter((element) => element.termsTitle !== undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerAttribute, telecom])

  const resetFormItems = () => {
    countStop()
    reset()
  }

  useEffect(() => {
    resetFormItems()
    if (isGreenpTracking) {
      landing_greenp()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pcode 별 통신사 가입 제한 팝업
  useEffect(() => {
    if (isJoinPossibleSuccess) {
      const telecomToJoinPossibleData = {
        '0': joinPossibleData?.SKTJoin,
        '1': joinPossibleData?.KTJoin,
        '2': joinPossibleData?.LGUPJoin,
      }

      // 가입 제한 통신사 목록 설정
      setBlockTelList(
        Object.entries(telecomToJoinPossibleData)
          .filter(([_key, value]) => value === 'N')
          .map(([key]) => key),
      )
      const isBlockTelecom = telecomToJoinPossibleData[fixedTelecom || telecom] === 'N'

      if (isBlockTelecom) {
        const dimLayer = document.createElement('div')
        Object.assign(dimLayer.style, {
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: '1000',
          pointerEvents: 'none',
        })

        document.body.appendChild(dimLayer)
        setTimeout(() => {
          alert(joinPossibleData?.JoinBlockMsg)
          window.self.close()
          window.history.back()
        }, 200)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJoinPossibleSuccess, telecom])

  const props: DefaultJoinFormPageProps = {
    count,
    otpCertVerify,
    otpCertRequest,
    registerAttribute,
    resetFormItems,
    registerAttributeExcludeAllTerms: registerAttributeExcludeAllTerms(),
    dynamicUiData:
      dynamicUiDefaultData.UIData !== UIData && UIData !== ''
        ? { UIData: parseUiData(UIData), ...dynamicUi }
        : dynamicUiDefaultData,
    blockTelList,
  }

  if (isDynamicUiPending) {
    return <Loading />
  }

  return (
    <FormProvider {...methods}>
      <RenderIf
        when={
          isOtpRequestPending ||
          isOtpVerifyPending ||
          isMvnoVerifyPending ||
          isVulnerableVerifyPending
        }
      >
        <Loading />
      </RenderIf>
      <RenderIf when={!!nmp_e01}>
        <NmpJoinFormPageView {...props} />
      </RenderIf>
      <RenderIf when={!nmp_e01}>
        <DefaultJoinFormPageView {...props} />
      </RenderIf>
      <RenderIf when={isTermsModalOpen}>
        <TermsModal
          isOpen={isTermsModalOpen}
          onClose={() => setIsTermsModalOpen(false)}
          termsUrl={termsUrl}
        />
      </RenderIf>
    </FormProvider>
  )
}

export default DefaultJoinFormPage
